import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import ThemePicker from '../components/ThemePicker';
import { ENV } from '../config/env';
import { validateDisplayName, generateDefaultDisplayName } from '../utils/displayHelpers';
import { 
  User, 
  Bell, 
  Shield, 
  CreditCard, 
  HelpCircle, 
  LogOut,
  Camera,
  Mail,
  Phone,
  Lock,
  Globe,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  Smartphone,
  Moon,
  Sun,
  Wallet,
  Key,
  ExternalLink,
  Gift,
  Clock,
  Tag,
  Palette
} from 'lucide-react';

const Settings = () => {
  const { user, logout, updateUser } = useAuth();
  const { getUserFeatureAccess, redeemCoupon } = useData();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    tabFromUrl && ['profile', 'notifications', 'security', 'payments', 'subscriptions', 'preferences', 'help'].includes(tabFromUrl)
      ? tabFromUrl
      : 'profile'
  );
  const [notifications, setNotifications] = useState({
    bookingUpdates: true,
    messages: true,
    promotions: false,
    emailNotifications: true,
    pushNotifications: true
  });
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState({ type: '', text: '' });
  const [isRedeeming, setIsRedeeming] = useState(false);
  
  // Profile editing states
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [displayNameError, setDisplayNameError] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);

  // Get offline jobs feature access
  const offlineJobsAccess = getUserFeatureAccess(user?.id, 'offline_jobs');
  const hasOfflineAccess = offlineJobsAccess && offlineJobsAccess.isActive && 
    (offlineJobsAccess.expiresAt === null || new Date(offlineJobsAccess.expiresAt) > new Date());

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage({ type: 'error', text: 'Please enter a coupon code' });
      return;
    }

    setIsRedeeming(true);
    setCouponMessage({ type: '', text: '' });

    try {
      const result = redeemCoupon(user.id, couponCode);
      
      if (result.success) {
        setCouponMessage({ type: 'success', text: result.message });
        setCouponCode('');
        setTimeout(() => {
          setCouponMessage({ type: '', text: '' });
        }, 3000);
      } else {
        setCouponMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setCouponMessage({ type: 'error', text: 'Failed to redeem coupon. Please try again.' });
    } finally {
      setIsRedeeming(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'subscriptions', label: 'Subscriptions', icon: Gift },
    { id: 'preferences', label: 'Preferences', icon: Globe },
    { id: 'help', label: 'Help & Support', icon: HelpCircle }
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const handleProfilePictureUpload = async (imageUrl) => {
    try {
      const userId = user?.uid || user?.id;
      console.log('Uploading profile picture:', imageUrl);
      
      // Update user avatar in users collection
      await updateUser({ avatar: imageUrl });
      console.log('Updated user avatar');
      
      // Also update profilePhoto in userProfiles collection for sync
      const { updateUserProfile, getUserProfile, createUserProfile } = await import('../services/firestoreService');
      
      // Check if profile exists first
      const existingProfile = await getUserProfile(userId);
      
      if (existingProfile.success && existingProfile.profile) {
        // Update existing profile
        await updateUserProfile(userId, { profilePhoto: imageUrl });
        console.log('Updated existing profile photo');
      } else {
        // Create new profile with the photo
        await createUserProfile(userId, {
          alias: user?.displayName || user?.email?.split('@')[0] || 'User',
          bio: '',
          profilePhoto: imageUrl,
          coverPhoto: '',
          isPublic: true,
          location: '',
          website: '',
          instagram: '',
          facebook: ''
        });
        console.log('Created new profile with photo');
      }
      
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (error) {
      console.error('Error updating profile picture:', error);
    }
  };

  const handleDisplayNameChange = (e) => {
    const value = e.target.value;
    setDisplayName(value);
    setDisplayNameError('');
  };

  const handleSaveProfile = () => {
    // If display name is empty, auto-generate from real name
    const nameToSave = displayName.trim() || generateDefaultDisplayName(user?.name || '');
    
    // Validate display name
    const validation = validateDisplayName(nameToSave);
    if (!validation.valid) {
      setDisplayNameError(validation.error);
      return;
    }

    // Update user profile
    updateUser({ displayName: nameToSave });
    setDisplayName(nameToSave); // Update the input with the final value
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 3000);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
              {profileSaved && (
                <div className="flex items-center gap-2 text-green-600 animate-pulse">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Saved!</span>
                </div>
              )}
            </div>

            {/* Profile Picture Upload */}
            <ProfilePictureUpload
              currentAvatar={user?.avatar}
              onUploadComplete={handleProfilePictureUpload}
              size="large"
              userId={user?.uid || user?.id}
            />

            {/* Privacy Notice */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Privacy & Display Name</p>
                  <p>Your <strong>real name</strong> remains private. Set a <strong>display name/alias</strong> below to show to other users on the platform. This helps protect your privacy while maintaining a friendly identity.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Real Name (Private) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name (Private)
                  <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded">Private</span>
                </label>
                <input
                  type="text"
                  value={user?.name}
                  disabled
                  className="form-input bg-gray-50 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Your real name is kept private and only visible to you</p>
              </div>

              {/* Display Name (Public Alias) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name (Public)
                  <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Visible to Others</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={displayName}
                    onChange={handleDisplayNameChange}
                    placeholder="e.g., Priya S."
                    className={`flex-1 px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)] transition-colors ${
                      displayNameError ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handleSaveProfile}
                    className="px-6 py-2 pf-btn-primary rounded-lg text-sm font-medium whitespace-nowrap transition-colors shadow-md"
                  >
                    Save
                  </button>
                </div>
                {displayNameError && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <span>⚠️</span> {displayNameError}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  This is what others will see on the platform. Leave empty for auto-generated name from your real name.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    defaultValue={user?.email}
                    className="form-input pr-10"
                    readOnly
                  />
                  <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  defaultValue={user?.phone}
                  className="form-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <input
                  type="text"
                  defaultValue={user?.role}
                  className="form-input capitalize"
                  readOnly
                />
              </div>
            </div>

          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Notification Preferences</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Booking Updates</h3>
                  <p className="text-sm text-gray-600">Get notified about booking status changes</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.bookingUpdates}
                    onChange={(e) => setNotifications({...notifications, bookingUpdates: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[color-mix(in_srgb,var(--pf-accent)_30%,transparent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--pf-accent)]"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Messages</h3>
                  <p className="text-sm text-gray-600">Receive notifications for new messages</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.messages}
                    onChange={(e) => setNotifications({...notifications, messages: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[color-mix(in_srgb,var(--pf-accent)_30%,transparent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--pf-accent)]"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Promotional</h3>
                  <p className="text-sm text-gray-600">Receive offers and promotional content</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.promotions}
                    onChange={(e) => setNotifications({...notifications, promotions: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[color-mix(in_srgb,var(--pf-accent)_30%,transparent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--pf-accent)]"></div>
                </label>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h3 className="font-medium text-gray-900 mb-4">Notification Channels</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">Email Notifications</p>
                        <p className="text-sm text-gray-600">{user?.email}</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailNotifications}
                        onChange={(e) => setNotifications({...notifications, emailNotifications: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[color-mix(in_srgb,var(--pf-accent)_30%,transparent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--pf-accent)]"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">Push Notifications</p>
                        <p className="text-sm text-gray-600">Browser notifications</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.pushNotifications}
                        onChange={(e) => setNotifications({...notifications, pushNotifications: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[color-mix(in_srgb,var(--pf-accent)_30%,transparent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--pf-accent)]"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Security Settings</h2>
            
            <div className="space-y-4">
              <div className="p-4 pf-organic-card">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[var(--pf-accent)] flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Two-Factor Authentication</h3>
                    <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
                  </div>
                </div>
                <button type="button" className="btn-outline text-sm border-[var(--pf-border)] text-[var(--pf-accent)] hover:bg-[var(--pf-accent-soft)]">
                  Enable 2FA
                </button>
              </div>

              <div className="p-4 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Key className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Change Password</h3>
                      <p className="text-sm text-gray-600">Update your password regularly for better security</p>
                    </div>
                  </div>
                  <button className="btn-outline text-sm">
                    Update
                  </button>
                </div>
              </div>

              <div className="p-4 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Login Activity</h3>
                      <p className="text-sm text-gray-600">View and manage your active sessions</p>
                    </div>
                  </div>
                  <button className="btn-outline text-sm">
                    View Activity
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
            
            <div className="space-y-4">
              <div className="p-4 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">**** **** **** 1234</p>
                      <p className="text-sm text-gray-600">Expires 12/25</p>
                    </div>
                  </div>
                  <button className="text-red-600 hover:text-red-700 text-sm font-medium">
                    Remove
                  </button>
                </div>
              </div>

              <button className="btn-outline w-full justify-center">
                Add Payment Method
              </button>

              <div className="pt-6 border-t border-gray-200">
                <h3 className="font-medium text-gray-900 mb-4">Payment History</h3>
                <div className="space-y-3">
                  {[1, 2, 3].map((_, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Booking #{1234 + i}</p>
                        <p className="text-sm text-gray-600">Oct {5 + i}, 2025</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">₹1,{800 + (i * 100)}</p>
                        <span className="badge badge-success">Completed</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">App Preferences</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Language</h3>
                    <p className="text-sm text-gray-600">Choose your preferred language</p>
                  </div>
                </div>
                <select className="form-input w-32 py-1">
                  <option>English</option>
                  <option>Hindi</option>
                  <option>Telugu</option>
                </select>
              </div>

              <div className="p-4 border border-[var(--pf-border)] rounded-xl bg-[var(--pf-surface)]">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-[var(--pf-accent-soft)] rounded-lg flex items-center justify-center">
                    <Palette className="w-5 h-5 text-[var(--pf-accent)]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[var(--pf-ink)]">Color Theme</h3>
                    <p className="text-sm text-[var(--pf-ink-muted)]">Pick your personal theme or use the platform default</p>
                  </div>
                </div>
                <ThemePicker />
              </div>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Currency</h3>
                    <p className="text-sm text-gray-600">Set your preferred currency</p>
                  </div>
                </div>
                <select className="form-input w-32 py-1">
                  <option>INR (₹)</option>
                  <option>USD ($)</option>
                  <option>EUR (€)</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'subscriptions':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Subscriptions & Features</h2>
            
            {/* Offline Jobs Feature */}
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                    <ExternalLink className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Offline Jobs Tracking</h3>
                    <p className="text-sm text-gray-600">Track external pet care jobs in one place</p>
                  </div>
                </div>
                {hasOfflineAccess ? (
                  offlineJobsAccess.expiresAt === null ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      Lifetime Access
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                      Active
                    </span>
                  )
                ) : (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                    Not Active
                  </span>
                )}
              </div>

              {hasOfflineAccess ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-medium text-green-900">Subscription Active</h4>
                    </div>
                    <div className="ml-8 space-y-1 text-sm text-green-800">
                      <p>Activated with coupon: <strong>{offlineJobsAccess.couponCode}</strong></p>
                      <p>Activated on: {new Date(offlineJobsAccess.activatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      {offlineJobsAccess.expiresAt ? (
                        <p>Expires on: {new Date(offlineJobsAccess.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      ) : (
                        <p>Access: Lifetime</p>
                      )}
                    </div>
                  </div>

                  {offlineJobsAccess.expiresAt && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900 mb-1">Renew Your Subscription</h4>
                          <p className="text-sm text-blue-800 mb-3">
                            Enter a coupon code to extend your access when it expires.
                          </p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              placeholder="Enter coupon code"
                              className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                              disabled={isRedeeming}
                            />
                            <button
                              onClick={handleRedeemCoupon}
                              disabled={isRedeeming || !couponCode.trim()}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isRedeeming ? 'Redeeming...' : 'Redeem'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Features Include:</h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Track all external pet care jobs
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Detailed financial records with receipts
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Compare online vs offline performance
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Export data for tax purposes
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Tag className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-900 mb-1">Activate with Coupon</h4>
                        <p className="text-sm text-blue-800 mb-3">
                          Enter a coupon code to activate offline jobs tracking. Price: ₹{ENV.SUBSCRIPTION.OFFLINE_JOBS_MONTHLY}/month
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            placeholder="Enter coupon code"
                            className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                            disabled={isRedeeming}
                          />
                          <button
                            onClick={handleRedeemCoupon}
                            disabled={isRedeeming || !couponCode.trim()}
                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isRedeeming ? 'Redeeming...' : 'Activate'}
                          </button>
                        </div>
                        <p className="text-xs text-blue-700 mt-2">
                          Try: TRIAL7, OFFLINE30, or TRACKFOREVER
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {couponMessage.text && (
                <div className={`mt-4 p-3 rounded-lg ${
                  couponMessage.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {couponMessage.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    )}
                    <p className={`text-sm ${couponMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                      {couponMessage.text}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Gift className="w-5 h-5 text-gray-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">How Coupons Work</h4>
                  <p className="text-sm text-gray-700">
                    Contact admin to get coupon codes for activating features. Coupons can provide monthly access or lifetime access depending on the code type.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'help':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Help & Support</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border border-gray-200 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">FAQ</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Find answers to common questions
                </p>
                <button className="btn-outline text-sm">View FAQ</button>
              </div>

              <div className="p-4 border border-gray-200 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">Contact Support</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Get help from our support team
                </p>
                <button className="btn-outline text-sm">Contact Us</button>
              </div>

              <div className="p-4 border border-gray-200 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">Terms of Service</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Read our terms and conditions
                </p>
                <button className="btn-outline text-sm">View Terms</button>
              </div>

              <div className="p-4 border border-gray-200 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">Privacy Policy</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Learn how we protect your data
                </p>
                <button className="btn-outline text-sm">View Policy</button>
              </div>
            </div>

            <div className="p-6 pf-organic-card">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[var(--pf-accent)] flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Need More Help?</h3>
                  <p className="text-gray-600">Our support team is here for you 24/7</p>
                </div>
              </div>
              <button className="btn-primary w-full justify-center">
                Contact Support
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="card">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        activeTab === tab.id
                          ? 'text-[var(--pf-accent)] bg-[var(--pf-accent-soft)]'
                          : 'text-gray-600 hover:text-[var(--pf-accent)] hover:bg-[var(--pf-accent-soft)]'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>

              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="card">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;